import { observer } from 'mobx-react';
import { BusinessEntity, OrganisationEntity } from 'Models/Entities';
import { store } from 'Models/Store';
import alertToast from 'Util/ToastifyUtils';
import { Colors, Display } from '../Button/Button';
import If from '../If/If';
import IndustryCodeSelector from '../Wizard/OrganisationRegistration/IndustryCodeSelector';
import OrganisationFields from './OrganisationFields';
import { useEffect, useState } from 'react';
import { AddressEntity } from '../../../Models/Entities';
import ButtonAsyncState from '../Button/ButtonAsyncState';
import { useEntityChangeTracker } from 'Hooks/useHasChanged';
import { runInAction } from 'mobx';
import { AddressModifiableFields } from 'Models/Entities/AddressEntity';
import { BusinessValidation } from '../BusinessNumberInputs/BusinessValidationModel';

export interface OrganisationDetailsProps {
	originalOrganisationEntity: OrganisationEntity,
}

const BusinessFields = ['abn', 'acn', 'trusteeACN', 'dateOfBirth', 'name'];
const PrimaryBusModifiableFields = [
	...BusinessFields,
	'name', 'phone', 'address', 'trusteeName', 'email',
] as (keyof BusinessEntity)[];

const OrgModifiableFields = ['industryCodes'] as (keyof OrganisationEntity)[];

const OrganisationDetails = observer((props: OrganisationDetailsProps) => {
	const {
		originalOrganisationEntity,
	} = props;

	const disabled = !(store.userType === 'SUPER_USER' || store.userPermissions.commonManageOrganisations !== 'NONE');

	const [organisation] = useState(() => {
		const org = new OrganisationEntity(originalOrganisationEntity);
		// Assign default record so we have a model to modify properties on
		runInAction(() => org.primaryBusinessEntity.address ??= new AddressEntity(originalOrganisationEntity.primaryBusinessEntity.address))
		return org;
	});

	const [abnacnDetail, setAbnAcnDetail] = useState<BusinessValidation>(new BusinessValidation());

	const {
		isChanged: businessIsChanged,
	} = useEntityChangeTracker(BusinessEntity, organisation.primaryBusinessEntity, PrimaryBusModifiableFields);

	const {
		isChanged: orgIsChanged,
	} = useEntityChangeTracker(OrganisationEntity, organisation, OrgModifiableFields);

	const {
		isChanged: addressIsChanged,
	} = useEntityChangeTracker(AddressEntity, organisation.primaryBusinessEntity.address!, AddressModifiableFields);

	useEffect(() => {
		const options = {
			abnEntityType: abnacnDetail?.abnExtraDetails.typeCode ?? ""
		}
		const businessFields = ['abn', 'acn', 'trusteeACN', 'dateOfBirth', 'name']
		businessFields.forEach(f => organisation.primaryBusinessEntity.validateField(f, options));
	}, [abnacnDetail]);

	const onSavePressed = async () => {
		try {
			runInAction(() => {
				organisation.industryCodesId = organisation.industryCodes?.id;
				if (!!organisation.primaryBusinessEntity.address) {
					organisation.primaryBusinessEntity.address.businessEntityId = organisation.primaryBusinessEntityId;
				}
			});
			await organisation.saveWithFetchBack({
				primaryBusinessEntity: {
					address: {},
				},
			}, {}, `
				primaryBusinessEntity {
					${BusinessEntity.getAttributes().join('\n')}
					address {
						${AddressEntity.getAttributes().join('\n')}
					}
				}
			`);

			originalOrganisationEntity.businessEntitys = [];
			originalOrganisationEntity.assignAttributes(organisation);
			alertToast('Organisation details have been successfully updated', 'success');
			organisation.businessEntitys
				.find(b => b.id === organisation.primaryBusinessEntity.id)
				?.assignAttributes(organisation.primaryBusinessEntity);


			window.location.reload();

		} catch (e) {
			console.error(e);
			alertToast('Organisation details could not be saved at this moment. Please refresh and try again.', 'error');
			return;
		}
	};

	function canSave() {
		const somethingHasChanged = businessIsChanged || orgIsChanged || addressIsChanged;
		return somethingHasChanged
			&& Object.keys(organisation.primaryBusinessEntity.errors).length === 0
			&& Object.keys(organisation.primaryBusinessEntity.address?.errors ?? {}).length === 0
			&& Object.keys(abnacnDetail.errors).length === 0
	}

	return (
		<>
			<h4>Organisation details</h4>
			<div className="organisation-details ctr">
				<div>
					<OrganisationFields
						businessEntity={organisation.primaryBusinessEntity}
						disabled={disabled}
						quickRegister={false}
						setAbnAcnDetail={setAbnAcnDetail}
					/>

					<IndustryCodeSelector
						model={organisation}
						modelProperty="industryCodes"
						className="industry-code"
						onChange={() => delete organisation.primaryBusinessEntity.errors.industryCode}
						errors={organisation.primaryBusinessEntity.errors.industryCode}
						isDisabled={disabled}
					/>

					<If condition={!disabled}>
						<div>
							<ButtonAsyncState
								className="save"
								colors={Colors.Primary}
								display={Display.Solid}
								onPress={onSavePressed}
								readonly={!canSave()}
							>
								Save Changes
							</ButtonAsyncState>
						</div>
					</If>
				</div>
			</div>
		</>
	);
});
export default OrganisationDetails;
